import { attach, combine, createEvent, sample } from "effector"
import { not, or, reset } from "patronum"
import { Validators, createEffectorForm, createGate, createToggle } from "@callcenter/effector-helpers"
import { localizationApi, userApi } from "@callcenter/api"
import { $callcenterService, $themeService, $userService } from "@callcenter/effector-services"

import { userAccountApi } from "../../api"
import { DEFAULT_FORM, REQUIRED_VALIDATION_TEXTS_MAP } from "./user-account.constants"
import { validateConfirmation, validatePassword } from "./user-account.validation"
import { transcriptTranslationModel } from "../transcript-translation"
import type { UserAccountFormState } from "../../shared/types"

export const $changePasswordAllowed = $userService.$permissions.map((userPermissions) =>
  userPermissions.has("allow_change_password")
)
export const $isTranscriptTranslationEnabled = $callcenterService.$permissions.map((permissions) =>
  permissions.has("transcript_translation")
)
// Voice-only agents pick the workspace layout themselves; omnichannel agents
// always get full screen, so the setting is hidden from them
export const $isAgentWorkspaceLayoutAllowed = combine(
  $callcenterService.$permissions,
  $userService.$permissions,
  (callcenterPermissions, userPermissions) =>
    callcenterPermissions.has("agent_workspace") && !userPermissions.has("omnichannel_workspace_enabled")
)
export const $isInterfaceSettingsAllowed = or(
  $callcenterService.$permissions.map((permissions) => permissions.has("language_selection")),
  $themeService.$isCustomThemeSettingsAvailable,
  $isAgentWorkspaceLayoutAllowed
)

export const [$isChangePasswordSectionVisible, changePasswordSectionShown, changePasswordSectionHidden] = createToggle()

const $skipPasswordValidation = or(not($isChangePasswordSectionVisible), not($changePasswordAllowed))

export const $isPageLoading = or(localizationApi.getInterfaceLanguageFx.pending, $userService.$isPermissionsLoading)
export const formSubmitted = createEvent()

export const submitFormFx = attach({
  effect: userAccountApi.userProfileSaveFx,
  source: {
    isTranscriptTranslationEnabled: $isTranscriptTranslationEnabled,
    transcriptPayload: transcriptTranslationModel.$formValue,
    availableSpeechLanguages: transcriptTranslationModel.$availableSpeechLanguages
  },
  mapParams: (
    formValue: UserAccountFormState,
    { transcriptPayload, availableSpeechLanguages, isTranscriptTranslationEnabled }
  ) => ({
    ...formValue,
    translate_from_languages: transcriptPayload.translate_from_languages,
    translate_to_language: transcriptPayload.translate_to_language,
    transcript_translation_enabled: transcriptPayload.transcript_translation_enabled,
    available_languages: availableSpeechLanguages.map((language) => language.key),
    is_transcript_translation_enabled: isTranscriptTranslationEnabled
  })
})

export const {
  $formValue,
  $formErrors,
  $isFormChanged,
  $isFormSubmitted,
  $isSubmitPending,
  handleChange,
  handleBlur,
  resetError,
  handleSubmit,
  setFormValue,
  resetForm
} = createEffectorForm({
  defaultValue: DEFAULT_FORM,
  validationSchema: {
    current_password: {
      beforeSubmit: [
        Validators.optional(
          Validators.required(REQUIRED_VALIDATION_TEXTS_MAP.current_password),
          $skipPasswordValidation
        )
      ]
    },
    password: {
      beforeSubmit: [
        Validators.optional(Validators.required(REQUIRED_VALIDATION_TEXTS_MAP.password), $skipPasswordValidation)
      ],
      change: [validatePassword]
    },
    password_confirmation: {
      beforeSubmit: [
        Validators.optional(
          Validators.required(REQUIRED_VALIDATION_TEXTS_MAP.password_confirmation),
          $skipPasswordValidation
        ),
        validateConfirmation
      ]
    }
  },
  submitFormFx
})

export const $isNeedSave = or($isFormChanged, transcriptTranslationModel.$isFormChanged)
export const [$isUserAccountOpened, userAccountToggled, userAccountOpened, userAccountClosed] = createToggle()

export const { PageGate: ModalGate, pageOpened: modalOpened, pageClosed: modalClosed } = createGate()
export const { PageGate: HeaderGate, pageOpened: headerOpened } = createGate()

sample({
  clock: formSubmitted,
  source: {
    formValue: $formValue,
    isNeedSave: $isNeedSave
  },
  filter: ({ isNeedSave }) => isNeedSave,
  target: handleSubmit
})

sample({
  clock: formSubmitted,
  source: {
    isNeedSave: $isNeedSave
  },
  filter: ({ isNeedSave }) => !isNeedSave,
  target: userAccountClosed
})

sample({
  clock: userApi.resetCurrentSessionFx.done,
  source: $callcenterService.$permissions,
  filter: (permissions) => permissions.has("language_selection"),
  target: localizationApi.getInterfaceLanguageFx
})

sample({
  clock: localizationApi.getInterfaceLanguageFx.doneData,
  source: $formValue,
  fn: (formValue, language) => ({ ...formValue, interface_language: language }),
  target: setFormValue
})

sample({
  clock: $userService.$userSettings,
  source: $formValue,
  filter: $isAgentWorkspaceLayoutAllowed,
  fn: (formValue, settings) => ({
    ...formValue,
    agent_workspace_display_layout: settings.agent_workspace_display_layout ?? undefined
  }),
  target: setFormValue
})

sample({
  clock: userAccountApi.userProfileSaveFx.done,
  target: userAccountClosed
})

sample({
  clock: modalClosed,
  target: resetForm
})

reset({
  clock: modalClosed,
  target: $isChangePasswordSectionVisible
})
